import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { applyChoice, OPEN_COOKIE_SETTINGS, openCookieSettings, ownRecorderOn, readChoice, saveChoice } from '../lib/cookieConsent.ts';
import { Icon } from './ui.tsx';

/**
 * daevio.com's own cookie banner. Shown only in builds where daevio records its own visitors (the measurement it
 * asks about); "Reject" and "Accept" weigh the same, closing it is a reject, and nothing is recorded before a yes.
 */
export function CookieBanner({ force = false }: { force?: boolean }) {
  const enabled = force || ownRecorderOn();
  const [open, setOpen] = useState(() => enabled && readChoice() === null);

  useEffect(() => {
    if (!enabled) return;
    // A choice already made applies on every page load (the recorder waits for it).
    const choice = readChoice();
    if (choice) applyChoice(choice);
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS, reopen);
  }, [enabled]);

  if (!enabled || !open) return null;

  const choose = (measurement: boolean) => {
    applyChoice(saveChoice(measurement));
    setOpen(false);
  };

  return (
    <section className="cookie-banner" role="dialog" aria-modal="false" aria-labelledby="cookie-banner-title" data-testid="cookie-banner">
      <button type="button" className="cookie-banner-close" aria-label="Close and reject measurement" onClick={() => choose(false)}>
        <Icon name="close" size={14} />
      </button>
      <h2 id="cookie-banner-title">Cookies and measurement</h2>
      <p>
        We use the storage needed to keep you signed in and remember this choice. With your consent we also record how people use daevio.com with our own
        script (form fields masked, no IP stored, nothing shared with advertisers), to make the site clearer. You can change your mind at any time from{' '}
        <em>Cookie settings</em> at the bottom of the page. Details in the <Link to="/cookies">Cookie Policy</Link>.
      </p>
      <div className="cookie-banner-actions">
        <button type="button" className="button" onClick={() => choose(false)}>
          Reject
        </button>
        <button type="button" className="button" onClick={() => choose(true)}>
          Accept
        </button>
      </div>
    </section>
  );
}

/** "Cookie settings" in a footer: opens the banner again. Hidden when the build has nothing to ask. */
export function CookieSettingsLink({ force = false }: { force?: boolean }) {
  if (!force && !ownRecorderOn()) return null;
  return (
    <button type="button" className="link-button" onClick={openCookieSettings}>
      Cookie settings
    </button>
  );
}
